import { Component } from '@angular/core';
import Swal from 'sweetalert2';
import { IGetList } from 'src/app/interfaces/Get/IGetList';
import { Subject, takeUntil } from 'rxjs';
import { IMedicalDeviceDetailed } from '../../../../../../interfaces/MedicalDevice/IMedicalDeviceDetailed';
import { MedicalDeviceService } from '../../../../../../services/medicalDevice.service';
import { MedicalDeviceAddEditComponent } from './medical-device-add-edit/medical-device-add-edit.component';
import { MedicalDeviceExpandComponent } from './medical-device-expand/medical-device-expand.component';
import { TranslateService } from '@ngx-translate/core';
import { COLORS } from 'src/styles/colors';
import { SwalToastService } from 'src/app/services/swal.service';
import { PermissionService } from 'src/app/services/permission.service';

@Component({
    selector: 'app-medical-device',
    templateUrl: './medical-device.component.html',
    styleUrls: ['./medical-device.component.css'],
    standalone: false
})
export class MedicalDeviceComponent {
  public addComp = MedicalDeviceAddEditComponent;
  public expandComp = MedicalDeviceExpandComponent;

  private destroy$ = new Subject<void>();

  medicalDevices?: IGetList<IMedicalDeviceDetailed>;
  isLoading: boolean = false;
  isNoKnownLoading: boolean = false;

  constructor(
    private readonly medicalDeviceService: MedicalDeviceService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly permissionService: PermissionService
  ) {}

  ngOnInit() {
    this.medicalDeviceService.medicalDeviceLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isLoading = data;
      });

    this.medicalDeviceService.medicalDeviceNoKnownLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isNoKnownLoading = data;
      });

    this.medicalDeviceService.medicalDeviceDataSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: IGetList<IMedicalDeviceDetailed>) => {
        this.medicalDevices = data;
      });

    this.medicalDeviceService.getData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  openModalFun(item?: IMedicalDeviceDetailed) {
    this.medicalDeviceService.medicalDeviceModalSub.next(true);
    this.medicalDeviceService.medicalDeviceEditSub.next(item);
  }

  deleteFun(medicalDeviceId: number) {
    Swal.fire({
      text: `${this.translate.instant(
        'deletePlaceholder2'
      )} ${this.translate.instant('Medical Device')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.medicalDeviceService
          .deleteMedicalDevice(medicalDeviceId.toString())
          .subscribe({
            next: (d) => {
              this.swalToastService.toastSuccess(
                'Medical Device deleted successfully'
              );
              this.medicalDeviceService.getData();
            },
            error: (err) => {
              this.swalToastService.toastError(
                'Unable to delete Medical Device'
              );
            },
          });
      } else if (result.isDenied) {
        Swal.fire(
          `${this.translate.instant('Medical device is not deleted')}`,
          '',
          'info'
        );
      }
    });
  }

  handleToggleFun(preview: boolean, switchValue: boolean, unknownid?: string) {
    if (!preview) {
      if (switchValue) {
        this.medicalDeviceService.medicalDeviceNoKnownLoadingSub.next(true);
        this.medicalDeviceService.addNoKnownMedicalDevice().subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'No known Medical Devices set successfully'
            );
            this.medicalDeviceService.getData();
          },
          error: (err) => {
            this.swalToastService.toastError(
              'Unable to set No Known Medical Devices'
            );
            console.error('Error setting No Known Medical Devices:', err);
          },
        });
      } else {
        if (unknownid) {
          this.medicalDeviceService.medicalDeviceNoKnownLoadingSub.next(true);
          this.medicalDeviceService
            .deleteNoKnownMedicalDevice(unknownid)
            .subscribe({
              next: () => {
                this.swalToastService.toastSuccess(
                  'No known Medical Devices unset successfully'
                );
                this.medicalDeviceService.getData();
              },
              error: (err) => {
                this.swalToastService.toastError(
                  'Unable to unset No Known Medical Devices'
                );
                console.error('Error unsetting No Known Medical Devices:', err);
              },
            });
        }
      }
    }
  }

  expandSet = new Set<number>();
  onExpandChange(id: number, checked: boolean): void {
    if (checked) {
      this.expandSet.add(id);
    } else {
      this.expandSet.delete(id);
    }
  }
}
